import React, { useState, useEffect, KeyboardEvent, useCallback, createContext, useContext, useReducer, useRef } from 'react';
import { Routes, Route, Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import './App.css';
import './components/common/SharedStyles.css';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ProfilePage from './components/auth/ProfilePage';
import MyWordsPage from './components/words/MyWordsPage';
import LearnPage from './components/learn/LearnPage';
import './components/auth/Auth.css';
import PageContainer from './components/common/PageContainer';
import WordItem from './components/common/WordItem';
import WordInfo from './components/common/WordInfo';

interface ApiResponse {
  message: string;
}

interface SearchResult {
  _id?: string;
  word: string;
  definition: string;
  definitions?: {
    text: string;
    partOfSpeech?: string;
    examples: string[];
  }[];
  phonetics?: string;
  phonics?: string;
  examples?: string[];
  tags?: string[];
  createdAt: Date | string;
  source?: 'openai' | 'fallback';
  searchCount?: number;
  metadata?: {
    responseTime: number;
    model: string;
    usage?: {
      prompt_tokens: number;
      completion_tokens: number;
      total_tokens: number;
    };
  };
}

// Define types for our global state
interface AppState {
  searchHistory: SearchResult[];
  historyLoaded: boolean;
  historyLoading: boolean;
  isLoggedIn: boolean;
}

// Actions for our reducer
type AppAction = 
  | { type: 'SET_HISTORY', payload: SearchResult[] }
  | { type: 'ADD_SEARCH', payload: SearchResult }
  | { type: 'SET_HISTORY_LOADING', payload: boolean }
  | { type: 'SET_HISTORY_LOADED', payload: boolean }
  | { type: 'SET_LOGGED_IN', payload: boolean }
  | { type: 'RESET_STATE' };

// Initial state
const initialState: AppState = {
  searchHistory: [],
  historyLoaded: false,
  historyLoading: false,
  isLoggedIn: false
};

// Create reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_HISTORY':
      return { ...state, searchHistory: action.payload, historyLoaded: true, historyLoading: false };
    case 'ADD_SEARCH':
      return { ...state, searchHistory: [action.payload, ...state.searchHistory].slice(0, 20) };
    case 'SET_HISTORY_LOADING':
      return { ...state, historyLoading: action.payload };
    case 'SET_HISTORY_LOADED':
      return { ...state, historyLoaded: action.payload };
    case 'SET_LOGGED_IN':
      return { ...state, isLoggedIn: action.payload };
    case 'RESET_STATE':
      return { ...initialState };
    default:
      return state;
  }
}

// Create context
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  loadSearchHistory: () => Promise<void>;
}

export const AppContext = createContext<AppContextType | null>(null);

// Additional CSS for App-specific components
const additionalStyles = `
.source-info {
  margin-top: 15px;
  font-size: 0.85rem;
  color: #999;
}

.source-label {
  margin: 0;
}

.source-openai {
  color: #10a37f;
  font-weight: bold;
}

.source-fallback {
  color: #ff9800;
  font-weight: bold;
}

.metadata-info {
  margin-top: 15px;
  font-size: 0.85rem;
  color: #999;
}

.metadata-info details {
  cursor: pointer;
}

.metadata-info summary {
  outline: none;
  color: #4285f4;
  font-weight: 500;
  margin-bottom: 8px;
}

.metadata-info summary:hover {
  text-decoration: underline;
}

.metadata-list {
  list-style: none;
  padding: 0;
  margin: 8px 0 0 0;
  background-color: #333;
  border-radius: 4px;
  padding: 10px;
}

.metadata-list li {
  margin-bottom: 5px;
}
`;

// Context provider component
export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  
  // Function to load search history - available throughout the app
  const loadSearchHistory = async () => {
    // If already loading or loaded, don't fetch again
    if (state.historyLoading || state.historyLoaded) {
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      dispatch({ type: 'SET_HISTORY_LOADING', payload: true });
      
      console.log('Loading search history - making API call');
      const response = await fetch('/api/search/history', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const history = await response.json();
        dispatch({ type: 'SET_HISTORY', payload: history });
        console.log('History loaded successfully');
      }
    } catch (error) {
      console.error('Error loading search history:', error);
      dispatch({ type: 'SET_HISTORY_LOADING', payload: false });
    }
  };
  
  // Effect to initialize auth state
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      dispatch({ type: 'SET_LOGGED_IN', payload: true });
      loadSearchHistory();
    }
    
    // Handle login/logout events
    const handleLogin = () => {
      dispatch({ type: 'SET_LOGGED_IN', payload: true });
      loadSearchHistory();
    };
    
    const handleLogout = () => {
      dispatch({ type: 'RESET_STATE' });
    };
    
    window.addEventListener('login', handleLogin);
    window.addEventListener('logout', handleLogout);
    
    return () => {
      window.removeEventListener('login', handleLogin);
      window.removeEventListener('logout', handleLogout);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // loadSearchHistory is created fresh each render with useCallback, so we don't include it here
  
  return (
    <AppContext.Provider value={{ state, dispatch, loadSearchHistory }}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the App context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

// Main App component
const App: React.FC = () => {
  // Add the additional styles to the document
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = additionalStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <AppProvider>
      <div className="App">
        <Routes>
          <Route path="/" element={<SearchPage />} />
          <Route path="/words" element={<MyWordsPage />} />
          <Route path="/learn" element={<LearnPage />} />
          <Route path="/profile" element={<ProfilePage />} />
        </Routes>
      </div>
    </AppProvider>
  );
};

// Global singleton for tracking API calls
const apiTracker = {
  historyLoading: false,
  historyLoaded: false,
  currentHistoryData: [] as SearchResult[]
};

// SearchPage component with all the search functionality
const SearchPage = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [showLogin, setShowLogin] = useState<boolean>(false);
  const [loginMessage, setLoginMessage] = useState<string>('');
  const [expandedHistoryItems, setExpandedHistoryItems] = useState<Record<string, boolean>>({});
  const [refreshing, setRefreshing] = useState<boolean>(false);
  
  // Track URL parameter search
  const urlParamHandled = useRef(false);
  const previousWordParam = useRef<string | null>(null);
  
  // Get app context
  const { state, dispatch } = useAppContext();
  const { isLoggedIn, searchHistory } = state;
  
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Handle search with a given term
  const handleSearchWithTerm = useCallback(async (term: string) => {
    if (!term.trim()) return;
    
    // Make sure user is logged in before searching
    if (!isLoggedIn) {
      setLoginMessage(`Please log in to search for "${term}"`);
      setShowLogin(true);
      return;
    }

    // Don't start a new search if one is already in progress
    if (isLoading) return;
    
    setIsLoading(true);
    setError('');

    try {
      // Call our API to get definition from GPT-4.1 nano
      const startTime = Date.now(); // Track client-side timing as well
      const token = localStorage.getItem('token');
      
      if (!token) {
        setShowLogin(true);
        setIsLoading(false);
        return;
      }
      
      const response = await fetch(`/api/search/define?word=${encodeURIComponent(term.trim())}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to get definition');
      }
      
      const data = await response.json();
      const clientTime = Date.now() - startTime;
      
      console.log(`Received definition for "${data.word}" in ${clientTime}ms`);
      
      // The API now returns structured data with definition and examples already parsed
      const newSearchResult = {
        ...data,
        createdAt: new Date()
      };
      
      setSearchResult(newSearchResult);
      
      // Add to search history if user is logged in
      if (isLoggedIn) {
        // Save to backend
        try {
          const saveResponse = await fetch('/api/search/history', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              word: data.word,
              definition: data.definition,
              examples: data.examples,
              phonetics: data.phonetics,
              phonics: data.phonics,
              tags: data.tags || [],
              source: data.source
            })
          });
          
          if (saveResponse.ok) {
            // Get the response data with updated vocabulary entry
            const savedData = await saveResponse.json();
            const vocabularyEntry = savedData.vocabularyEntry;
            
            // Check if this word is already in the history
            const existingIndex = state.searchHistory.findIndex(
              item => item.word.toLowerCase() === data.word.toLowerCase()
            );
            
            if (existingIndex >= 0) {
              // Word exists, update it rather than add a new entry
              const updatedHistory = [...state.searchHistory];
              updatedHistory[existingIndex] = {
                ...updatedHistory[existingIndex],
                definition: data.definition,
                phonetics: data.phonetics,
                phonics: data.phonics,
                examples: data.examples || [],
                tags: data.tags || [],
                source: data.source,
                createdAt: new Date(),
                searchCount: vocabularyEntry.searchCount || 1
              };
              dispatch({ type: 'SET_HISTORY', payload: updatedHistory });
            } else {
              // Add as new search
              dispatch({ 
                type: 'ADD_SEARCH', 
                payload: {
                  _id: vocabularyEntry._id || '',
                  word: data.word,
                  definition: data.definition,
                  phonetics: data.phonetics,
                  phonics: data.phonics,
                  examples: data.examples || [],
                  tags: data.tags || [],
                  source: data.source,
                  createdAt: new Date(),
                  searchCount: 1
                }
              });
            }
          } else {
            console.error('Failed to save search history');
          }
        } catch (saveError) {
          console.error('Error saving search history:', saveError);
        }
      }
    } catch (err) {
      setError('Failed to get definition. Please try again.');
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn, isLoading, dispatch, state.searchHistory]);
  
  // Simple search button handler
  const handleSearch = () => {
    handleSearchWithTerm(searchTerm);
  };

  // Enter key handler for search input
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Login modal close handler
  const handleLoginClose = () => {
    setShowLogin(false);
    setLoginMessage('');
  };
  
  // Handle URL parameter for word searches
  useEffect(() => {
    const wordParam = searchParams.get('word');
    
    // Only handle the parameter if it exists and has changed
    if (wordParam && 
        location.pathname === '/' && 
        (previousWordParam.current !== wordParam || !urlParamHandled.current)) {
      
      // Update tracking refs
      previousWordParam.current = wordParam;
      urlParamHandled.current = true;
      
      // Update the search term in the input field
      setSearchTerm(wordParam);
      
      if (isLoggedIn) {
        // User is logged in, perform the search
        handleSearchWithTerm(wordParam);
      } else {
        // User is not logged in, show login prompt
        setLoginMessage(`Please log in to search for "${wordParam}"`);
        setShowLogin(true);
      }
    }
  }, [searchParams, location.pathname, isLoggedIn, handleSearchWithTerm]);
  
  // Handle login state changes
  useEffect(() => {
    // Reset URL parameter handling when login state changes
    if (isLoggedIn && !urlParamHandled.current) {
      const wordParam = searchParams.get('word');
      if (wordParam) {
        setSearchTerm(wordParam);
        handleSearchWithTerm(wordParam);
        urlParamHandled.current = true;
      }
    }
  }, [isLoggedIn, searchParams, handleSearchWithTerm]);

  // Toggle history item expansion
  const toggleHistoryItemExpand = (itemId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedHistoryItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  
  // Remove history item
  const handleRemoveHistoryItem = async (itemId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await fetch(`/api/search/vocabulary/${itemId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // Update the local state by removing the deleted word
        dispatch({
          type: 'SET_HISTORY',
          payload: state.searchHistory.filter(item => item._id !== itemId)
        });
      } else {
        console.error('Failed to remove history item');
      }
    } catch (error) {
      console.error('Error removing history item:', error);
    }
  };

  // Handle refresh for history items - maintain expanded state
  const handleHistoryItemRefresh = (itemId?: string) => {
    // Skip if no ID is provided
    if (!itemId) return;
    
    // Keep the item expanded after refresh - do this first to ensure state is set
    setExpandedHistoryItems(prev => ({
      ...prev,
      [itemId]: true
    }));
    
    // Find the word in the history
    const item = searchHistory.find(item => item._id === itemId);
    if (!item || !item.word) {
      console.error(`Could not find history item with ID ${itemId}`);
      return;
    }
    
    // Update just this one item without refreshing everything
    const updateSingleHistoryItem = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        // Get the updated definition from the server
        const response = await fetch(`/api/search/define?word=${encodeURIComponent(item.word)}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const updatedData = await response.json();
          
          // Update this item in the search history, preserving its position
          const updatedHistory = searchHistory.map(historyItem => {
            if (historyItem._id === itemId) {
              console.log(`Updating search history item ${itemId} without changing position`);
              return {
                ...historyItem,
                definition: updatedData.definition,
                definitions: updatedData.definitions,
                phonetics: updatedData.phonetics,
                phonics: updatedData.phonics,
                examples: updatedData.examples,
                source: updatedData.source,
                searchCount: (historyItem.searchCount || 0) + 1,
                // Don't update the createdAt as that would change its position
              };
            }
            return historyItem;
          });
          
          // Update the state with the updated history
          dispatch({
            type: 'SET_HISTORY',
            payload: updatedHistory
          });
        }
      } catch (error) {
        console.error('Error updating single history item:', error);
      }
    };
    
    // Run the update
    updateSingleHistoryItem();
  };

  // Handle refreshing definition
  const handleRefreshDefinition = async () => {
    if (!searchResult || !searchResult.word || refreshing) return;
    
    setRefreshing(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token available');
        setRefreshing(false);
        return;
      }
      
      // First, force fetch a new definition from OpenAI
      const response = await fetch(`/api/search/define?word=${encodeURIComponent(searchResult.word)}&forceApi=true`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to get updated definition');
      }
      
      const data = await response.json();
      
      // Now save the updated definition to the database
      const saveResponse = await fetch('/api/search/history', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          word: data.word,
          definitions: data.definitions,
          phonetics: data.phonetics,
          phonics: data.phonics,
          tags: searchResult.tags || [],
          source: data.source
        })
      });
      
      if (!saveResponse.ok) {
        throw new Error('Failed to save updated definition');
      }
      
      // Update the search result with new definition
      setSearchResult({
        ...data,
        createdAt: new Date()
      });
      
      // Also update in history if exists
      const existingIndex = searchHistory.findIndex(
        item => item.word.toLowerCase() === data.word.toLowerCase()
      );
      
      if (existingIndex !== -1) {
        const updatedHistory = [...searchHistory];
        updatedHistory[existingIndex] = {
          ...updatedHistory[existingIndex],
          ...data
        };
        
        dispatch({
          type: 'SET_HISTORY',
          payload: updatedHistory
        });
      }
    } catch (error) {
      console.error('Error refreshing definition:', error);
      alert('Failed to refresh definition. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <PageContainer>
      <div className="search-section">
        <p className="search-intro">Search for word definitions using GPT-4.1 nano:</p>
        {!isLoggedIn && (
          <p className="login-required-message">
            <i>Login is required to search for word definitions.</i>
          </p>
        )}
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Enter a word or phrase"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            autoCapitalize="none"
          />
          <button 
            className="search-button"
            onClick={handleSearch}
            disabled={isLoading}
          >
            {isLoading ? 'Searching...' : 'Search'}
          </button>
        </div>
        
        {error && <p className="error-text">{error}</p>}
        
        {searchResult && (
          <div className="result-container">
            <h3 className="item-title">{searchResult.word}</h3>
            
            <WordInfo
              definition={searchResult.definition}
              definitions={searchResult.definitions}
              phonetics={searchResult.phonetics}
              phonics={searchResult.phonics}
              examples={searchResult.examples}
              source={searchResult.source}
              tags={searchResult.tags}
              onRefresh={handleRefreshDefinition}
              refreshing={refreshing}
            />
            
            {searchResult.metadata && (
              <div className="metadata-info">
                <details>
                  <summary>API Metadata</summary>
                  <ul className="metadata-list">
                    <li>Response Time: {searchResult.metadata.responseTime}ms</li>
                    <li>Model: {searchResult.metadata.model}</li>
                    {searchResult.metadata.usage && (
                      <>
                        <li>Prompt Tokens: {searchResult.metadata.usage.prompt_tokens}</li>
                        <li>Completion Tokens: {searchResult.metadata.usage.completion_tokens}</li>
                        <li>Total Tokens: {searchResult.metadata.usage.total_tokens}</li>
                      </>
                    )}
                  </ul>
                </details>
              </div>
            )}
          </div>
        )}
        
        {isLoggedIn && searchHistory.length > 0 && (
          <div className="words-container">
            <div className="words-header-container">
              <h3 className="history-title">Recent Searches</h3>
            </div>
            <ul className="words-list">
              {searchHistory.slice(0, 5).map((item, index) => (
                <WordItem
                  key={item._id ? `history-${item._id}` : `history-index-${index}`}
                  item={item}
                  isExpanded={item._id ? expandedHistoryItems[item._id] : false}
                  onToggleExpand={toggleHistoryItemExpand}
                  onRemove={handleRemoveHistoryItem}
                  onClick={handleSearchWithTerm}
                  formatSubtitle={(item) => {
                    return item.searchCount > 1 
                      ? `Searched ${item.searchCount} times · ${new Date(item.createdAt).toLocaleString()}`
                      : new Date(item.createdAt).toLocaleString();
                  }}
                  className="vocab-item"
                  onRefresh={() => handleHistoryItemRefresh(item._id)}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
      {showLogin && <Login message={loginMessage} onClose={handleLoginClose} />}
    </PageContainer>
  );
};

export default App; 